<template>
  <div class="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
    <bar-chart class="col-span-2 bg-white overflow-hidden shadow rounded-lg mb-4 p-4"
               :options="chartOptions"
               :chart-data="ordersPerClient"/>
    <data-table :entity="'client'"
                :columns="columns"
                :data="sortedResults"
                :is-loading="loading"
                :disablePermissionChecks="true"
                rowNavigationKey="client_uuid"
                operations="refresh,edit"
                class="col-span-2"
                ref="table">
      <template v-slot:total="{ row }">
        <strong>{{ row.total }}</strong>
      </template>
    </data-table>
  </div>
</template>
<script>
  import get from 'lodash/get'
  import groupBy from 'lodash/groupBy'
  import uniq from 'lodash/uniq'
  import format from 'date-fns/format'
  import BarChart from 'src/components/Charts/BarChart'
  import ReportService from 'src/services/ReportService'
  import orderBy from 'lodash/orderBy'

  export default {
    components: {
      BarChart
    },
    props: {
      dateFrom: {
        type: String,
        default: `${new Date().getFullYear()}-01-01`
      },
      dateTill: {
        type: String,
        default: format(new Date(), 'yyyy-MM-dd'),
      },
      job_board_id: {
        type: [String, Number],
        default: 2
      },
      allJobBoards: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        ordersPerClientTotals: {
          results: []
        },
        chartColors: ['#5145cd', '#31c48d', '#0694a2', '#F9703E', '#e02424'],
        loading: false,
      }
    },
    computed: {
      ordersPerClientReport () {
        return {
          model: 'orders',
          group: 'job_boards,clients',
          select: 'id,total,client,job_board',
          date_range: 'custom',
          date_from: this.dateFrom,
          date_till: this.dateTill,
          sort: 'date_month',
        }
      },
      columns() {
        return [
          {
            label: this.$t('generic.client'),
            prop: 'client_name'
          },
          {
            label: this.$t('reports.createdOrders'),
            prop: 'total'
          }
        ]
      },
      sortedResults() {
        return orderBy(this.ordersPerClientTotals.results, 'total', 'desc')
      },
      chartOptions () {
        if (!this.allJobBoards) {
          return {}
        }
        return {
          scales: {
            yAxes: [{
              stacked: true
            }],
            xAxes: [{
              stacked: true
            }]
          },
          legend: {
            display: true
          },
          responsive: true,
          maintainAspectRatio: false
        }
      },
      ordersPerClient () {
        let validClients = this.ordersPerClientTotals.results
        if (!this.allJobBoards) {
          validClients = validClients.filter(r => {
            if (!r.job_board_id) {
              return
            }
            return r.job_board_id.toString() === this.job_board_id.toString()
          })
        }
        let other = {total: 0, client_name: 'Others'}
        if (validClients.length > 8 && !this.allJobBoards) {
          let others = validClients.filter(c => c.total < 10)
          validClients = validClients.filter(c => c.total > 10)
          others.forEach(c => {
            other.total += c.total
          })
          validClients.push(other)
        }
        const data = {
          labels: validClients.map(r => r.client_name),
          datasets: [
            {
              label: this.$t('reports.postedJobsPerClient'),
              backgroundColor: '#31c48d',
              data: validClients.map(r => r.total)
            }
          ]
        }
        if (this.allJobBoards) {
          return this.generateDataForAllJobBoards(validClients)
        }
        return data
      }
    },
    methods: {
      generateDataForAllJobBoards(validClients) {
        let clients = validClients.map(r => r.client_name)
        clients = uniq(clients)
        let groupedJobBoards = groupBy(validClients, 'job_board_id')
        let totalJobBoards = Object.keys(groupedJobBoards).length
        let datasets = []
        for (let i = 0; i < totalJobBoards; i++) {
          const jobBoardKey = Object.keys(groupedJobBoards)[i]
          let label = get(groupedJobBoards[jobBoardKey], '[0].job_board_name', '')
          let backgroundColor = this.chartColors[i % (this.chartColors.length - 1)]

          let data = []
          clients.forEach(client => {
            const jobBoardClients = groupedJobBoards[jobBoardKey] || []
            const matchingClient = jobBoardClients.find(c => c.client_name === client) || {total: 0}
            data.push(matchingClient.total)
          })
          datasets.push({
            label,
            backgroundColor,
            data: data
          })
        }
        return {
          labels: clients,
          datasets
        }
      },
      async getOrdersPerClientReport () {
        try {
          this.loading = true
          const {data} = await ReportService.get(this.ordersPerClientReport)
          this.ordersPerClientTotals = data
          this.$emit('data-loaded', this.ordersPerClientTotals)
        } finally {
          this.loading = false
        }
      }
    },
    watch: {
      dateFrom () {
        this.getOrdersPerClientReport()
      },
      dateTill () {
        this.getOrdersPerClientReport()
      }
    },
    mounted () {
      this.getOrdersPerClientReport()
    }
  }
</script>
<style>
</style>
